
const libPictView = require('pict-view');

/**
 * EmailNotificationsList — the #/EmailNotifications landing.
 *
 * A list of the customer's Email Rules (Meadow EmailRule entities): each shows its handler, the documents it
 * targets (type/status/projects), whether it is enabled, and links to the editor. Data lives in
 * EmailNotificationsProvider (AppData.EmailNotifications); this view derives loading/empty/error slots and
 * repaints declaratively. Styled to match the Approval Workflows list it sits beside.
 */
class EmailNotificationsList extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get provider()
	{
		return this.pict.providers.EmailNotifications;
	}

	onBeforeRender(pRenderable)
	{
		const tmpState = this.provider ? this.provider.state : { List: [], ListLoading: false, ListError: '' };
		const tmpApp = this.pict.AppData.EmailNotifications || {};
		tmpApp.LoadingSlot = tmpState.ListLoading ? [ {} ] : [];
		tmpApp.ErrorSlot = tmpState.ListError ? [ { Message: tmpState.ListError } ] : [];
		tmpApp.EmptySlot = (!tmpState.ListLoading && !tmpState.ListError && tmpState.List.length === 0) ? [ {} ] : [];
		tmpApp.CanEdit = this.provider ? this.provider.canEdit : false;
		tmpApp.NewButtonSlot = tmpApp.CanEdit ? [ {} ] : [];
		tmpApp.CountSlot = (!tmpState.ListLoading && !tmpState.ListError && tmpState.List.length > 0) ? [ { Count: tmpState.List.length } ] : [];
		// Per-row filter text + enabled styling.
		tmpApp.Rows = (tmpState.List || []).map((pRow) => Object.assign({}, pRow, {
			Filter: `${pRow.HandlerLabel} ${pRow.DocumentTypeText} ${pRow.StatusText}`.toLowerCase(),
			EnabledClass: pRow.Enabled ? 'is-on' : 'is-off',
			EnabledText: pRow.Enabled ? 'On' : 'Off',
			ScheduledSlot: pRow.Scheduled ? [ {} ] : [],
		}));
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	/** Client-side filter of the rule rows by handler / document type / status (show/hide, no re-render). */
	filterRows(pQuery)
	{
		const tmpQuery = String(pQuery || '').trim().toLowerCase();
		let tmpVisible = 0;
		Array.prototype.forEach.call(document.querySelectorAll('.emnl-row'), (pRow) =>
		{
			const tmpText = pRow.getAttribute('data-filter') || '';
			const tmpMatch = !tmpQuery || tmpText.indexOf(tmpQuery) > -1;
			pRow.style.display = tmpMatch ? '' : 'none';
			if (tmpMatch) { tmpVisible++; }
		});
		const tmpEmpty = document.getElementById('EmailNotifications-NoMatch');
		if (tmpEmpty) { tmpEmpty.style.display = (tmpVisible === 0 && tmpQuery) ? '' : 'none'; }
	}
}

module.exports = EmailNotificationsList;

module.exports.default_configuration =
{
	ViewIdentifier: 'EmailNotificationsList',
	DefaultRenderable: 'EmailNotificationsListWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,

	CSS: /*css*/`
		.emnl-wrap { padding: 1.25rem 1.5rem 2rem; max-width: 82rem; }
		.emnl-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin-bottom: 0.5rem; }
		.emnl-count { display: inline-flex; align-items: center; font-size: 0.72rem; font-weight: 700; color: var(--bulma-link, #156dd1); background: #eef3fb; border-radius: 999px; padding: 0.1rem 0.6rem; margin-left: 0.5rem; vertical-align: middle; }
		.emnl-toolbar { margin: 0.85rem 0 1rem; max-width: 24rem; }
		.emnl-list { border: 1px solid var(--bulma-border, #e6e9ef); border-radius: 12px; background: var(--bulma-scheme-main, #fff); overflow: hidden; }
		.emnl-row { position: relative; display: flex; align-items: center; gap: 0.85rem; padding: 0.7rem 1rem; text-decoration: none; color: inherit; cursor: pointer;
			border-bottom: 1px solid var(--bulma-border, #f0f2f5); border-left: 3px solid transparent; transition: background 0.1s, border-left-color 0.1s; }
		.emnl-row:last-child { border-bottom: none; }
		.emnl-row:hover { background: var(--bulma-scheme-main-bis, #f6f8fc); border-left-color: var(--bulma-link, #156dd1); }
		.emnl-enabled { flex-shrink: 0; width: 2.4rem; text-align: center; font-size: 0.64rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; padding: 0.12rem 0; border-radius: 999px; }
		.emnl-enabled.is-on { background: #e4f5ea; color: #2e9e5b; }
		.emnl-enabled.is-off { background: var(--bulma-scheme-main-bis, #f1f2f4); color: #9aa3b2; }
		.emnl-row-ic { flex-shrink: 0; color: var(--bulma-link, #156dd1); }
		.emnl-row-name { flex: 1 1 9rem; min-width: 8rem; font-weight: 700; color: #22334c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
		.emnl-row-meta { display: flex; gap: 0.4rem; flex-wrap: wrap; flex-shrink: 1; align-items: center; justify-content: flex-end; }
		.emnl-chip { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.72rem; font-weight: 600; padding: 0.15rem 0.55rem; border-radius: 999px; background: var(--bulma-scheme-main-bis, #f1f4f9); color: #5a6b82; white-space: nowrap; max-width: 15rem; overflow: hidden; text-overflow: ellipsis; }
		.emnl-chip .icon { font-size: 0.7rem; }
		.emnl-chip.is-sched { background: #fdf3e0; color: #b26a2c; }
		.emnl-row-arrow { flex-shrink: 0; color: var(--bulma-border, #cfd6e2); transition: color 0.12s, transform 0.12s; }
		.emnl-row:hover .emnl-row-arrow { color: var(--bulma-link, #156dd1); transform: translateX(2px); }
		.emnl-empty { padding: 2.5rem 1rem; }
		.emnl-spinner { display: inline-block; width: 1.6rem; height: 1.6rem; border: 3px solid var(--bulma-border, #dbdbdb); border-top-color: var(--bulma-link, #156dd1); border-radius: 50%; animation: emnl-spin 0.8s linear infinite; }
		@keyframes emnl-spin { to { transform: rotate(360deg); } }
	`,

	Templates:
	[
		{
			Hash: 'EmailNotifications-List-Wrap',
			Template: /*html*/`
				<div class="emnl-wrap">
					<div class="emnl-header">
						<div>
							<h1 class="title is-4"><span class="icon"><i class="fas fa-envelope"></i></span> Email Notifications{~TS:EmailNotifications-List-Count:AppData.EmailNotifications.CountSlot~}</h1>
							<p class="subtitle is-6 has-text-grey">Rules that send automated emails when documents change status or on a schedule. Open one to edit who is notified and when.</p>
						</div>
						<div class="field is-grouped">
							{~TS:EmailNotifications-List-NewButton:AppData.EmailNotifications.NewButtonSlot~}
						</div>
					</div>

					{~TS:EmailNotifications-List-Error:AppData.EmailNotifications.ErrorSlot~}

					<div class="emnl-toolbar">
						<div class="control has-icons-left">
							<input class="input" type="text" placeholder="Filter rules…" oninput="_Pict.views.EmailNotificationsList.filterRows(this.value)">
							<span class="icon is-left"><i class="fas fa-magnifying-glass"></i></span>
						</div>
					</div>

					<div class="emnl-list">
						{~TS:EmailNotifications-List-Loading:AppData.EmailNotifications.LoadingSlot~}
						{~TS:EmailNotifications-List-Row:AppData.EmailNotifications.Rows~}
						<div id="EmailNotifications-NoMatch" class="emnl-empty has-text-grey has-text-centered" style="display:none"><span class="icon is-large"><i class="fas fa-magnifying-glass fa-lg"></i></span><p class="mt-2">No rules match your filter.</p></div>
						{~TS:EmailNotifications-List-Empty:AppData.EmailNotifications.EmptySlot~}
					</div>
				</div>`
		},
		{
			Hash: 'EmailNotifications-List-Count',
			Template: /*html*/`<span class="emnl-count">{~D:Record.Count~}</span>`
		},
		{
			Hash: 'EmailNotifications-List-NewButton',
			Template: /*html*/`<button class="button is-link" onclick="window.location.hash = '#/EmailRule/New'"><span class="icon"><i class="fas fa-plus"></i></span><span>New rule</span></button>`
		},
		{
			Hash: 'EmailNotifications-List-Row',
			Template: /*html*/`
				<a class="emnl-row" href="#/EmailRule/{~D:Record.IDEmailRule~}" data-filter="{~D:Record.Filter~}">
					<span class="emnl-enabled {~D:Record.EnabledClass~}">{~D:Record.EnabledText~}</span>
					<span class="icon emnl-row-ic"><i class="fas fa-envelope-open-text"></i></span>
					<span class="emnl-row-name">{~D:Record.HandlerLabel~}</span>
					<span class="emnl-row-meta">
						<span class="emnl-chip"><span class="icon"><i class="fas fa-file-lines"></i></span> {~D:Record.DocumentTypeText~}</span>
						<span class="emnl-chip"><span class="icon"><i class="fas fa-flag"></i></span> {~D:Record.StatusText~}</span>
						<span class="emnl-chip"><span class="icon"><i class="fas fa-diagram-project"></i></span> {~D:Record.ProjectsText~}</span>
						{~TS:EmailNotifications-List-SchedChip:Record.ScheduledSlot~}
					</span>
					<span class="icon emnl-row-arrow"><i class="fas fa-chevron-right"></i></span>
				</a>`
		},
		{
			Hash: 'EmailNotifications-List-SchedChip',
			Template: /*html*/`<span class="emnl-chip is-sched"><span class="icon"><i class="fas fa-clock"></i></span> Scheduled</span>`
		},
		{
			Hash: 'EmailNotifications-List-Empty',
			Template: /*html*/`
				<div class="emnl-empty has-text-grey has-text-centered">
					<span class="icon is-large"><i class="fas fa-envelope fa-2x"></i></span>
					<p class="mt-3">No email rules are configured for your organization yet.</p>
				</div>`
		},
		{
			Hash: 'EmailNotifications-List-Loading',
			Template: /*html*/`<div class="emnl-empty has-text-grey has-text-centered"><span class="emnl-spinner"></span><p class="mt-3">Loading email rules…</p></div>`
		},
		{
			Hash: 'EmailNotifications-List-Error',
			Template: /*html*/`<div class="notification is-danger is-soft py-2 px-3 mb-3"><span class="icon"><i class="fas fa-triangle-exclamation"></i></span> <span>{~D:Record.Message~}</span></div>`
		}
	],

	Renderables:
	[
		{ RenderableHash: 'EmailNotificationsListWrap', TemplateHash: 'EmailNotifications-List-Wrap', ContentDestinationAddress: '#PlatformConfiguration-Application-Container' }
	]
};
